import { Clock } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { DateTimePicker } from '@/components/DateTimePicker';
import { NotificationBadge } from '@/components/NotificationBadge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { type FormValues } from './validationSchema';

export const ChangesIntervalPicker = (): JSX.Element => {
  const form = useFormContext<FormValues>();

  const from = useWatch<FormValues>({
    name: 'from',
  });

  const to = useWatch<FormValues>({
    name: 'to',
  });

  const showDeleted = useWatch<FormValues>({
    name: 'showDeleted',
  });

  const definedValues =
    (to !== undefined ? 1 : 0) +
    (from !== undefined ? 1 : 0) +
    (showDeleted === true ? 1 : 0);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="relative">
          <Clock className="mr-1 h-6 stroke-2" />
          {definedValues > 0 ? (
            <NotificationBadge count={definedValues} />
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-max">
        <div className="">
          <div className="flex flex-row items-baseline justify-between">
            <h3 className="text-lg mb-4 mt-0">
              <FormattedMessage id="search.changes.title" />
            </h3>
            <Button
              type="button"
              variant="link"
              onClick={() => {
                form.setValue('from', undefined);
                form.setValue('to', undefined);
                form.setValue('showDeleted', false);
              }}
            >
              <FormattedMessage id="search.changes.clear" />
            </Button>
          </div>
          <div className="flex flex-row space-x-4 items-center">
            <DateTimePicker name="from" />
            <p>and</p>
            <DateTimePicker name="to" />
          </div>
          <div>
            <FormField
              control={form.control}
              name="showDeleted"
              render={({ field }) => (
                <FormItem className="flex items-center">
                  <FormControl>
                    <Checkbox
                      disabled={from === undefined && to === undefined}
                      onCheckedChange={e => field.onChange(e)}
                      checked={field.value}
                      className="mt-2 w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-primary-300 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-primary-600 dark:ring-offset-gray-800"
                    />
                  </FormControl>
                  <FormLabel className="text-gray-500 dark:text-gray-300 ml-2">
                    <FormattedMessage id="search.changes.showDeleted" />
                  </FormLabel>
                </FormItem>
              )}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
